import {
	Add,
	Check,
	Delete,
	Edit,
	Email,
	MoreVert,
	RemoveRedEye,
	SwapHoriz,
	ThumbDown,
	ThumbUp,
} from "@mui/icons-material";
import { Badge, Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import {
	formatCurrencyNumber,
	formatDate,
	formatDateTime,
	getDocumentStatusesForFilter,
} from "@repo/legacy/components/helper/Index.js";
import List from "@repo/legacy/components/shared/lists/List";
import ConfirmationDialog from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import ConfirmationWithMessageDialog from "@repo/legacy/dialogs/ConfirmationWithMessageForwardRefDialog";
import Dropdown from "@repo/legacy/components/shared/sections/Dropdown";
import {
	DocumentStatus,
	DocumentType,
	useDownloadDocumentLazyQuery,
	useConvertOfferToInvoiceMutation,
	useGetAllProjectsNameLazyQuery,
	type Project,
} from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAllCommercialDocumentsDataSource } from "@repo/legacy/components/shared/datasources/AllCommercialDocumentsDataSource";
import { DownloadButton } from "@repo/shared/components";

import type {
	GridCellParams,
	GridColDef,
	GridRowId,
	GridRowSelectionModel,
} from "@mui/x-data-grid";
import type { ConfirmationDialogHandle } from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import type { ConfirmationWithMessageDialogHandle } from "@repo/legacy/dialogs/ConfirmationWithMessageForwardRefDialog";
import type { CommercialDocumentSummary } from "@repo/shared-invoice/types";
import type { EmailDialogHandle } from "@repo/shared-invoice/dialogs/EmailForwardRefDialog";
import type { PreviewDocumentDialogHandle } from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import type { OptionType } from "@repo/server-api/types";
import { EmailDialog } from "../dialogs/EmailForwardRefDialog";
import { PreviewDocumentDialog } from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import { Invoice } from "../types/CommercialDocumentTypes";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import InfoDialog, { InfoDialogHandle } from "@repo/legacy/dialogs/InfoForwardRefDialog";

export const OfferListPage = () => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();

	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	// Filter states
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProject, setSelectedProject] = useState<OptionType | null>(null);

	const [downloadDocument] = useDownloadDocumentLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [convertOfferToInvoice] = useConvertOfferToInvoiceMutation();

	const [getAllProjects] = useGetAllProjectsNameLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			first: 100,
		},
		onCompleted(data) {
			if (data.allProjects?.nodes) {
				setProjects(data.allProjects.nodes as Project[]);
			}
		},
	});

	const getProjects = (searchName: string) => {
		getAllProjects({
			variables: {
				first: 100,
				name: searchName,
			},
		});
	};

	useEffect(() => {
		getAllProjects();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const allOffersDataSource = useAllCommercialDocumentsDataSource() as ReturnType<
		typeof useAllCommercialDocumentsDataSource
	> & {
		rows: CommercialDocumentSummary[];
	};

	const status = useParams<{ status: string }>().status || DocumentStatus.Draft;

	const confirmationRef = useRef<ConfirmationDialogHandle | null>(null);
	const confirmationWithMessageRef = useRef<ConfirmationWithMessageDialogHandle | null>(null);
	const emailRef = useRef<EmailDialogHandle | null>(null);
	const previewDocumentRef = useRef<PreviewDocumentDialogHandle | null>(null);
	const infoRef = useRef<InfoDialogHandle | null>(null);

	const columns: GridColDef<CommercialDocumentSummary>[] = useMemo(
		() => [
			{
				field: "client.name",
				headerName: t("commercial-document"),
				headerAlign: "center",
				flex: 1,
				sortable: true,
				valueGetter: (value, row) => row.client.name,
				renderCell(params: GridCellParams<CommercialDocumentSummary>) {
					const dueDays =
						params.row.status === DocumentStatus.Accepted
							? 10
							: dayjs(params.row.dueDate).diff(dayjs(), "day");
					return (
						<Box className="w-100 padding11">
							<Box className="w-60 left">
								<strong
									style={
										!params.row.number
											? {
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-start",
													padding: "10px",
													marginTop: "5px",
													float: "left",
												}
											: {}
									}
									className="font21">
									{params.row.client.name}
								</strong>
								{params.row.number && (
									<>
										<br />
										<div className="font12" style={{ marginTop: 5 }}>
											{t("offer-number")} {params.row.number}
											{params.row.issueDate && (
												<span>
													{` ${t("from")}`} {formatDate(params.row.issueDate, i18n.language)}
												</span>
											)}
											{params.row.dueDate && (
												<span
													className={
														dueDays < 0 ? "redText" : dueDays < 7 ? "orangeText" : "greyText"
													}>
													{" "}
													- {t("valid-until")} {formatDate(params.row.dueDate, i18n.language)}
												</span>
											)}
										</div>
									</>
								)}
							</Box>
							<Box className="w-40 right textRight">
								{params.row.status === DocumentStatus.Accepted && (
									<Badge
										style={{
											justifyContent: "center",
											alignItems: "center",
											display: "flex",
											backgroundColor: "#D1E7DD",
											color: "#0F5132",
											borderRadius: "10px",
											padding: "5px",
											fontSize: "10px",
											fontWeight: "bold",
											width: "70px",
											height: "15px",
											margin: "17px",
											float: "right",
										}}>
										{t("accepted")}
									</Badge>
								)}
								{params.row.status === DocumentStatus.Rejected && (
									<Badge
										onClick={() => {
											infoRef.current?.show({
												text: params.row.rejectionReason || t("no-reason-provided"),
											});
										}}
										style={{
											cursor: "pointer",
											justifyContent: "center",
											alignItems: "center",
											display: "flex",
											backgroundColor: "#F8D7DA",
											color: "#842029",
											borderRadius: "10px",
											padding: "5px",
											fontSize: "10px",
											fontWeight: "bold",
											width: "70px",
											height: "15px",
											margin: "17px",
											float: "right",
										}}>
										{t("rejected")}
									</Badge>
								)}
								<strong
									style={
										params.row.number
											? {
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													padding: "10px",
													margin: "5px",
													float: "right",
												}
											: {
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-start",
													padding: "10px",
													marginTop: "5px",
													float: "right",
												}
									}
									className="font21">
									{formatCurrencyNumber(params.row.grossTotal)}
								</strong>
							</Box>
							<div className="clear" />
						</Box>
					);
				},
			},
			{
				field: "project.name",
				headerName: t("project"),
				headerAlign: "center",
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.project?.name || "",
				renderCell: (params: GridCellParams) => {
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}>
							<span>{params.row.project?.name || "-"}</span>
						</div>
					);
				},
			},
			{
				field: "project.number",
				headerName: t("project-number"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.project?.number || "",
				renderCell: (params: GridCellParams) => {
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}>
							<span>{params.row.project?.number || "-"}</span>
						</div>
					);
				},
			},
			{
				field: "dueDate",
				headerName: t("valid-until"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.dueDate,
				renderCell: (params: GridCellParams) => (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							height: "100%",
						}}>
						<span>{params.row.dueDate ? formatDate(params.row.dueDate, i18n.language) : "-"}</span>
					</div>
				),
			},
			{
				field: "sentDate",
				headerName: t("sent-date"),
				headerAlign: "center",
				width: 120,
				sortable: true,
				valueGetter: (value, row) => row.sentDate,
				renderCell: (params: GridCellParams) => {
					const dateTime = params.row.sentDate
						? formatDateTime(params.row.sentDate, i18n.language)
						: "-";
					const isSent = !!params.row.sentDate;
					let date = "";
					let time = "";
					if (isSent) {
						const [datePart, timePart] = dateTime.split(" ");
						date = datePart;
						time = timePart;
					}

					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								alignItems: "center",
								textAlign: "center",
							}}>
							<span>
								{isSent ? (
									<>
										{date}
										<br />
										{time}
									</>
								) : (
									"-"
								)}
							</span>
						</div>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				headerAlign: "center",
				sortable: false,
				width: 200,
				renderCell: (params: GridCellParams) => {
					const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

					const isDraft = params.row.status === DocumentStatus.Draft;
					const isFinalized =
						params.row.status === DocumentStatus.Finalized ||
						params.row.status === DocumentStatus.Sent;
					const isAccepted = params.row.status === DocumentStatus.Accepted;
					const isRejected = params.row.status === DocumentStatus.Rejected;

					const openDelete = (id: string) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-delete-this-document"),
							onYes: () => {
								if (allOffersDataSource.deleteData) allOffersDataSource.deleteData(id);
							},
						});
					};

					const openEdit = (p: CommercialDocumentSummary) => {
						navigate(`/offers/${status}/${encodeURIComponent(p.id)}`);
					};

					const openFinalize = (p: CommercialDocumentSummary) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-finalize-this-document"),
							onYes: () => {
								const issueDate = dayjs().format("YYYY-MM-DD");
								if (allOffersDataSource.finalizeData)
									allOffersDataSource.finalizeData(p.id, issueDate);
							},
						});
					};

					const openEmail = (p: CommercialDocumentSummary) => {
						emailRef.current?.show({
							invoice: p as Invoice,
							id: p.id,
							onSend: (data) => {
								if (allOffersDataSource.sendCommercialDocumentEmail)
									allOffersDataSource.sendCommercialDocumentEmail(p.type, p.id, p.client.id, {
										recipients: data.recipients,
										ccRecipients: data.ccRecipients,
										bccRecipients: data.bccRecipients,
										subject: data.subject,
										body: data.body,
										attachments: data.attachments,
										shouldMergeAttachments: data.shouldMergeAttachments,
									});
							},
						});
					};

					const openPreview = async (id: string) => {
						const result = await downloadDocument({
							variables: { id, shouldDownload: false },
						});
						const url = result?.data?.downloadDocument;
						if (url) previewDocumentRef.current?.show({ url });
					};

					const startDownload = async (p: CommercialDocumentSummary) => {
						const result = await downloadDocument({
							variables: { id: p.id, shouldDownload: true },
						});
						const url = result?.data?.downloadDocument;
						if (url) window.open(url, "_blank");
					};

					const openAccept = (p: CommercialDocumentSummary) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-accept-this-offer"),
							onYes: async () => {
								if (allOffersDataSource.acceptDocument) allOffersDataSource.acceptDocument(p.id);
							},
						});
					};

					const openReject = (p: CommercialDocumentSummary) => {
						confirmationWithMessageRef.current?.show({
							text: t("do-you-want-to-reject-this-offer"),
							onYes: async (reason: string) => {
								if (allOffersDataSource.rejectDocument)
									allOffersDataSource.rejectDocument(p.id, reason);
							},
						});
					};

					const openConvertToInvoice = (p: CommercialDocumentSummary) => {
						confirmationRef.current?.show({
							text: t("do-you-want-to-convert-this-offer-to-invoice"),
							onYes: async () => {
								const result = await convertOfferToInvoice({ variables: { id: p.id } });
								const invoiceId = result?.data?.convertOfferToInvoice?.invoice?.id;
								if (invoiceId) {
									navigate(`/invoices/${DocumentStatus.Draft}/${encodeURIComponent(invoiceId)}`);
								} else {
									allOffersDataSource.refresh?.();
								}
							},
						});
					};

					const handleClick = (event: React.MouseEvent<HTMLElement>) => {
						setAnchorEl(event.currentTarget);
					};
					const handleClose = () => {
						setAnchorEl(null);
					};

					return (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								padding: "15px",
							}}>
							{isDraft && (
								<Button
									data-testid="edit-offer-button"
									onClick={() => {
										openEdit(params.row);
										handleClose();
									}}
									title={t("edit")}>
									<Edit className="font32 listButtonColor" />
								</Button>
							)}
							{!params.row.isInvalid && (
								<DownloadButton
									buttonProps={{ className: "listButtonColor" }}
									iconProps={{ className: "font32" }}
									onDownload={() => startDownload(params.row)}
									title={t("download-document")}
								/>
							)}
							<IconButton
								id="offer-actions-button"
								data-testid="offer-actions-menu-button"
								aria-controls={Boolean(anchorEl) ? "offer-actions-menu" : undefined}
								aria-haspopup="true"
								aria-expanded={Boolean(anchorEl) ? "true" : undefined}
								onClick={handleClick}>
								<MoreVert className="font32 listButtonColor" />
							</IconButton>
							<Menu
								id="offer-actions-menu"
								aria-labelledby="offer-actions-button"
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								anchorOrigin={{ vertical: "top", horizontal: "left" }}
								transformOrigin={{ vertical: "top", horizontal: "left" }}>
								{!params.row.isInvalid && isDraft && (
									<MenuItem
										data-testid="finalize-offer-menu-item"
										onClick={() => {
											openFinalize(params.row);
											handleClose();
										}}>
										<Check className="font32 listButtonColor" sx={{ mr: 2 }} /> {t("finalize")}
									</MenuItem>
								)}
								{(isFinalized || isAccepted) && !params.row.isInvalid && (
									<MenuItem
										data-testid="email-offer-menu-item"
										onClick={() => {
											openEmail(params.row);
											handleClose();
										}}>
										<Email className="font32 listButtonColor" sx={{ mr: 2 }} /> {t("email")}
									</MenuItem>
								)}
								{!params.row.isInvalid && (
									<MenuItem
										data-testid="preview-offer-menu-item"
										onClick={() => {
											openPreview(params.row.id);
											handleClose();
										}}>
										<RemoveRedEye className="font32 listButtonColor" sx={{ mr: 2 }} />{" "}
										{t("preview")}
									</MenuItem>
								)}
								{(isFinalized || isRejected) && (
									<MenuItem
										data-testid="accept-offer-menu-item"
										onClick={() => {
											openAccept(params.row);
											handleClose();
										}}>
										<ThumbUp className="font32 listButtonColor" sx={{ mr: 2 }} />{" "}
										{t("accept-offer")}
									</MenuItem>
								)}
								{isFinalized && (
									<MenuItem
										data-testid="reject-offer-menu-item"
										onClick={() => {
											openReject(params.row);
											handleClose();
										}}>
										<ThumbDown className="font32 listButtonColor" sx={{ mr: 2 }} />{" "}
										{t("reject-offer")}
									</MenuItem>
								)}
								{isAccepted && (
									<MenuItem
										data-testid="convert-offer-to-invoice-menu-item"
										onClick={() => {
											openConvertToInvoice(params.row);
											handleClose();
										}}>
										<SwapHoriz className="font32 listButtonColor" sx={{ mr: 2 }} />{" "}
										{t("convert-to-invoice")}
									</MenuItem>
								)}
								{isDraft && (
									<MenuItem
										data-testid="delete-offer-menu-item"
										onClick={() => {
											openDelete(params.row.id);
											handleClose();
										}}>
										<Delete className="font32 listButtonColor" sx={{ mr: 2 }} /> {t("delete")}
									</MenuItem>
								)}
							</Menu>
						</div>
					);
				},
			},
		],
		[t, i18n.language],
	);

	// Update status filter when the route status param changes
	useEffect(() => {
		if (allOffersDataSource.setFilter) {
			allOffersDataSource.setFilter({
				...allOffersDataSource.filter,
				status: getDocumentStatusesForFilter(status),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const allColumns = useMemo(() => {
		switch (status.toLocaleUpperCase()) {
			case DocumentStatus.Draft:
				return columns.filter((col) => col.field !== "sentDate" && col.field !== "dueDate");
			default:
				return columns;
		}
	}, [status, columns]);

	// Stable type array — must not be inline to avoid triggering List's useEffect on every render
	const offerTypes = useMemo(() => [DocumentType.Offer], []);

	// Filter rows by selected project (frontend filtering until backend support is added)
	const filteredRows = useMemo(() => {
		if (!selectedProject?.item?.id) return allOffersDataSource.rows;
		return allOffersDataSource.rows.filter((row) => row.project?.id === selectedProject.item?.id);
	}, [allOffersDataSource.rows, selectedProject]);

	const [openRange, setOpenRange] = useState(false);

	return (
		<>
			<ConfirmationDialog ref={confirmationRef} />

			<ConfirmationWithMessageDialog ref={confirmationWithMessageRef} />

			<EmailDialog ref={emailRef} />

			<PreviewDocumentDialog ref={previewDocumentRef} />

			<InfoDialog ref={infoRef} />

			<h3 className="purpleHeader">{t(`offers-${status}`)}</h3>
			<List
				columns={allColumns as GridCellParams<CommercialDocumentSummary>[]}
				redInvalidRows={true}
				dataSource={{ ...allOffersDataSource, rows: filteredRows }}
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={(newRowSelectionModel) => {
					setRowSelectionModel(newRowSelectionModel);
				}}
				type={offerTypes}
				customRowHeight={{ getRowHeight: () => "auto" }}>
				<Box className="flex-between" sx={{ gap: 2, flexWrap: "wrap" }}>
					{/* Project Filter */}
					<Box sx={{ minWidth: 200 }}>
						<Dropdown
							dropdownType="project"
							doNotShowMenu
							elements={projects}
							element={{
								title: selectedProject?.title || "",
								inputValue: selectedProject?.inputValue || "",
								item: selectedProject?.item || null,
							}}
							setElement={(p) => {
								setSelectedProject(p);
							}}
							searchLabel={t("project")}
							searchPlaceholder={t("search-for-project")}
							getProjects={getProjects}
						/>
					</Box>

					{/* From till to */}
					<Box sx={{ minWidth: 270, display: "flex", gap: 1, alignItems: "center" }}>
						<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
							<DateRangePicker
								open={openRange}
								onClose={() => setOpenRange(false)}
								onOpen={() => setOpenRange(true)}
								sx={{ width: "100%" }}
								label={t("from") + " - " + t("to")}
								value={[
									dayjs(allOffersDataSource.filter?.startDate) || null,
									dayjs(allOffersDataSource.filter?.endDate) || null,
								]}
								onChange={(e) => {
									allOffersDataSource.setFilter?.({
										...allOffersDataSource.filter,
										startDate: e[0] ? dayjs(e[0]).startOf("day").format("YYYY-MM-DD") : null,
										endDate: e[1] ? dayjs(e[1]).endOf("day").format("YYYY-MM-DD") : null,
									});
								}}
								slotProps={{
									textField: {
										onClick: () => setOpenRange(!openRange),
										error: false,
									},
								}}
							/>
						</LocalizationProvider>
					</Box>

					{/* Create Button */}
					{status.toLocaleUpperCase() === DocumentStatus.Draft && (
						<Box sx={{ ml: "auto" }}>
							<Button
								className="blueButton"
								data-testid="create-offer-button"
								onClick={() => {
									navigate(`/offers/${status}/new`);
								}}>
								{t("create-offer")}
								<Add />
							</Button>
						</Box>
					)}
				</Box>
			</List>
		</>
	);
};
